import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './dialog';
import { Button } from './button';
import { Clock, RefreshCw, Loader2, MessageSquare, ArrowLeft, Eye, Edit3, Star, CheckCircle, AlertTriangle } from 'lucide-react';
import { FeedbackForm, GameSessionSummary } from './FeedbackForm';
import { formatToIST } from '../../lib/dateUtils';

export function RetryRequestModal({
  open,
  onClose,
  capturedCount = 0,
  totalCount = 7,
  capturedList = [],
  retryStatus = 'NONE', // 'NONE', 'PENDING'
  retryCount = 0,
  existingFeedback = null,
  onRequestRetry,
  onFeedbackUpdate,
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState(null);
  // viewMode: 'main' | 'view' | 'edit' | 'new'
  const [viewMode, setViewMode] = useState('main');
  const [confirmFreshQuestions, setConfirmFreshQuestions] = useState(false);

  const handleRequestRetry = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      await onRequestRetry();
      setSubmitSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to submit request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFeedbackSuccess = (feedbackData) => {
    // Update parent state with the full submitted feedback so View shows it immediately
    if (onFeedbackUpdate) {
      onFeedbackUpdate({
        submitted: true,
        dinosaursCaptured: capturedCount,
        submittedAt: Date.now(),
        ...feedbackData,
      });
    }
    setViewMode('main');
  };

  const isPending = retryStatus === 'PENDING' || submitSuccess;
  const canRequest = retryStatus === 'NONE' && !submitSuccess;
  const hasFeedback = existingFeedback && existingFeedback.submitted;


  // View Feedback Mode
  if (viewMode === 'view' && hasFeedback) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent hideCloseButton onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()} className="sm:max-w-lg bg-gray-900 border-gray-700 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl text-cyan-400">
              <Eye className="h-6 w-6" />
              Your Submitted Feedback
            </DialogTitle>
            <DialogDescription className="text-gray-300">
              Submitted on {formatToIST(existingFeedback.submittedAt)}
            </DialogDescription>
          </DialogHeader>

          <button
            onClick={() => setViewMode('main')}
            className="flex items-center gap-1 text-sm text-gray-400 hover:text-gray-200 mb-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          {/* Game Session Summary */}
          <GameSessionSummary
            dinosaursCaptured={existingFeedback.dinosaursCaptured || capturedCount}
            totalDinosaurs={totalCount}
            capturedList={capturedList}
            feedbackType="TIME_UP"
          />

          {/* Rating Display */}
          <div className="bg-gray-800 rounded-lg p-4">
            <p className="text-gray-400 text-sm mb-2">Your Rating</p>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={`w-6 h-6 ${
                    star <= existingFeedback.rating
                      ? 'text-yellow-400 fill-yellow-400'
                      : 'text-gray-500'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Feedback Content */}
          <div className="space-y-4">
            <div className="bg-gray-800 rounded-lg p-4">
              <p className="text-gray-400 text-sm mb-2">Game Experience</p>
              <p className="text-white">{existingFeedback.gameExperience}</p>
            </div>

            <div className="bg-gray-800 rounded-lg p-4">
              <p className="text-gray-400 text-sm mb-2">Deluge Learning</p>
              <p className="text-white">{existingFeedback.delugeLearning}</p>
            </div>

            <div className="bg-gray-800 rounded-lg p-4">
              <p className="text-gray-400 text-sm mb-2">Suggestions</p>
              <p className="text-white">{existingFeedback.suggestions}</p>
            </div>
          </div>

          {/* Edit Button */}
          <Button
            onClick={() => setViewMode('edit')}
            className="w-full bg-cyan-600 hover:bg-cyan-700 text-white"
          >
            <Edit3 className="h-4 w-4 mr-2" />
            Edit Feedback
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  // Edit/New Feedback Mode
  if (viewMode === 'edit' || viewMode === 'new') {
    const isEdit = viewMode === 'edit' && hasFeedback;
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent hideCloseButton onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()} className="sm:max-w-lg bg-gray-900 border-gray-700 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl text-cyan-400">
              <MessageSquare className="h-6 w-6" />
              {isEdit ? 'Edit Your Feedback' : 'Share Your Feedback'}
            </DialogTitle>
            <DialogDescription className="text-gray-300">
              {isEdit
                ? 'Update your feedback to help us improve DRE World.'
                : 'Help us improve DRE World with your valuable feedback.'}
            </DialogDescription>
          </DialogHeader>

          <button
            onClick={() => setViewMode('main')}
            className="flex items-center gap-1 text-sm text-gray-400 hover:text-gray-200 mb-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          <FeedbackForm
            feedbackType="TIME_UP"
            dinosaursCaptured={capturedCount}
            totalDinosaurs={totalCount}
            capturedList={capturedList}
            initialValues={isEdit ? {
              rating: existingFeedback.rating,
              gameExperience: existingFeedback.gameExperience,
              delugeLearning: existingFeedback.delugeLearning,
              suggestions: existingFeedback.suggestions,
            } : null}
            onSubmitSuccess={handleFeedbackSuccess}
          />
        </DialogContent>
      </Dialog>
    );
  }

  // Main View
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent hideCloseButton onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()} className="sm:max-w-md bg-gray-900 border-gray-700 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl text-red-400">
            <Clock className="h-6 w-6" />
            Time's Up!
          </DialogTitle>
          <DialogDescription className="text-gray-300">
            Your time has expired for this session.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* Progress Display */}
          <div className="bg-gray-800 rounded-lg p-4 text-center">
            <p className="text-gray-400 text-sm mb-2">Your Progress</p>
            <p className="text-3xl font-bold text-amber-400">
              {capturedCount} / {totalCount}
            </p>
            <p className="text-gray-400 text-sm mt-1">
              dinosaurs captured
            </p>
          </div>

          {/* Retry Count Info */}
          {retryCount > 0 && (
            <div className="text-center text-sm text-gray-400">
              Previous retry requests: {retryCount}
            </div>
          )}

          {/* Status Message */}
          {isPending && (
            <div className="bg-yellow-900/30 border border-yellow-600/50 rounded-lg p-3 text-center">
              <p className="text-yellow-400 font-medium">
                {submitSuccess ? 'Request Submitted!' : 'Request Pending'}
              </p>
              <p className="text-yellow-300/70 text-sm mt-1">
                Your retry request is awaiting admin approval.
              </p>
            </div>
          )}

          {/* Feedback Submitted Indicator */}
          {hasFeedback && (
            <div className="bg-green-900/30 border border-green-600/50 rounded-lg p-3 text-center">
              <div className="flex items-center justify-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <p className="text-green-400 font-medium">Feedback Submitted</p>
              </div>
              <p className="text-green-300/70 text-sm mt-1">
                Thank you for your feedback!
              </p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-900/30 border border-red-600/50 rounded-lg p-3 text-center">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Fresh Questions Confirmation - only show if can request */}
          {canRequest && (
            <div className="bg-amber-900/20 border border-amber-600/30 rounded-lg p-3">
              <div className="flex items-start gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-amber-200 text-sm">
                  If approved, your progress will be reset and <strong>fresh questions</strong> will be assigned to you.
                </p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmFreshQuestions}
                  onChange={(e) => setConfirmFreshQuestions(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-amber-500 focus:ring-amber-500"
                />
                <span className="text-gray-300 text-sm">I understand and want to proceed</span>
              </label>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2">
          {/* Request Another Chance Button */}
          <Button
            onClick={handleRequestRetry}
            disabled={!canRequest || isSubmitting || !confirmFreshQuestions}
            className="w-full bg-green-600 hover:bg-green-700 text-white disabled:bg-gray-600"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Submitting...
              </>
            ) : isPending ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Request Pending Approval
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Request Another Chance
              </>
            )}
          </Button>

          {/* Feedback Buttons - based on whether feedback exists */}
          {hasFeedback ? (
            <div className="flex gap-2">
              <Button
                onClick={() => setViewMode('view')}
                variant="outline"
                className="flex-1 bg-gray-800 hover:bg-gray-700 text-gray-300 border-gray-600"
              >
                <Eye className="h-4 w-4 mr-2" />
                View Feedback
              </Button>
              <Button
                onClick={() => setViewMode('edit')}
                variant="outline"
                className="flex-1 bg-gray-800 hover:bg-gray-700 text-gray-300 border-gray-600"
              >
                <Edit3 className="h-4 w-4 mr-2" />
                Edit
              </Button>
            </div>
          ) : (
            <Button
              onClick={() => setViewMode('new')}
              variant="outline"
              className="w-full bg-gray-800 hover:bg-gray-700 text-gray-300 border-gray-600"
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              Give Feedback
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default RetryRequestModal;
